import { Injectable } from "@angular/core";
import { ENABLE_CHAT, UserPermission } from "@ankaadia/ankaadia-shared";
import { translateSafely } from "@ankaadia/ankaadia-shared-frontend";
import { PrimeIcons } from "primeng-v17/api";
import { map, Observable, OperatorFunction, Subject } from "rxjs";
import { TaskInboxService } from "../../features/process/task-inbox/task-inbox.service";
import { OrganizationFactoryService } from "../../organization-specific/organization-specific-factory.service";
import { AdditionalModule, SettingsService } from "../../shared/services/settings.service";
import { CustomMenuItem, IndexedMenuItem } from "./menu.model";

enum MenuSection {
  TalentPool = "TalentPool",
  Tasks = "Tasks",
  Profile = "Profile",
  EmployerSearch = "EmployerSearch",
  Documents = "Documents",
  Candidates = "Candidates",
  Processes = "Processes",
  Education = "Education",
  Templates = "Templates",
  Settings = "Settings",
  Notifications = "Notifications",
}

@Injectable({ providedIn: "root" })
export class MenuService {
  private readonly taskInboxCount$ = this.getTaskInboxCount();

  private readonly MenuSectionMap: Record<MenuSection, CustomMenuItem[]> = {
    [MenuSection.Tasks]: this.settings.menu?.tasks?.enabled
      ? [
          {
            label: translateSafely("tasks.title"),
            icon: PrimeIcons.BRIEFCASE,
            routerLink: ["processes/tasks"],
            routerLinkActiveOptions: { exact: true },
            permissions: [UserPermission.Candidate],
            badge: this.taskInboxCount$,
            testId: "menu-item-tasks",
          },
        ]
      : [],
    [MenuSection.Profile]: this.settings.menu?.profile?.enabled
      ? [
          {
            label: translateSafely("candidate.profile"),
            icon: PrimeIcons.PENCIL,
            routerLink: ["profile"],
            routerLinkActiveOptions: { exact: true },
            permissions: [UserPermission.Candidate],
            testId: "menu-item-profile",
          },
        ]
      : [],
    [MenuSection.EmployerSearch]: this.settings.menu?.employerSearch?.enabled
      ? [
          {
            label: translateSafely("employerSearch.title"),
            icon: PrimeIcons.LINK,
            routerLink: ["employers"],
            routerLinkActiveOptions: { exact: false },
            permissions: [UserPermission.Candidate],
          },
        ]
      : [],
    [MenuSection.Documents]: [
      {
        label: translateSafely("attachments.title"),
        icon: PrimeIcons.FILE,
        routerLink: ["documents"],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.Candidate],
        testId: "menu-item-documents",
      },
    ],
    [MenuSection.Candidates]: [
      {
        label: translateSafely("candidates.edit"),
        icon: PrimeIcons.PENCIL,
        routerLink: ["candidates/edit"],
        routerLinkActiveOptions: { exact: false },
        permissions: [UserPermission.User],
        testId: "menu-item-candidate-profile",
      },
      this.licensed({
        label: translateSafely("collections.title"),
        icon: PrimeIcons.LIST,
        routerLink: ["collections/edit"],
        routerLinkActiveOptions: { exact: false },
        permissions: [UserPermission.User],
        testId: "menu-item-candidate-collection",
      }),
      this.licensed({
        label: translateSafely("candidates.events"),
        icon: PrimeIcons.CALENDAR,
        routerLink: ["candidates/events"],
        routerLinkActiveOptions: { exact: false },
        permissions: [UserPermission.User],
        testId: "menu-item-candidate-events",
      }),
      {
        label: translateSafely("candidates.view"),
        icon: PrimeIcons.ID_CARD,
        routerLink: ["candidates/view"],
        routerLinkActiveOptions: { exact: false },
        permissions: [UserPermission.User],
        testId: "menu-item-candidate-view",
      },
    ],
    [MenuSection.Processes]: [
      {
        label: translateSafely("myTasks.title"),
        icon: PrimeIcons.INBOX,
        routerLink: ["processes/tasks"],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.User],
        testId: "menu-item-my-tasks",
        badge: this.taskInboxCount$,
      },
      this.licensed({
        label: translateSafely("processes.edit"),
        icon: PrimeIcons.BRIEFCASE,
        routerLink: ["processes/edit"],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.User],
        testId: "menu-item-process-edit",
      }),
      {
        label: translateSafely("processes.view"),
        icon: PrimeIcons.TH_LARGE,
        routerLink: ["processes/view"],
        routerLinkActiveOptions: { exact: false },
        permissions: [UserPermission.User],
        testId: "menu-item-running-process",
      },
      this.licensed({
        label: translateSafely("processModels.title"),
        icon: PrimeIcons.LIST,
        routerLink: [`process-models/${this.settings.organizationId}`],
        routerLinkActiveOptions: { exact: false },
        permissions: [UserPermission.ProcessAdministrator],
        testId: "menu-item-process-model",
      }),
      this.licensed({
        label: translateSafely("autoCollectionTemplate.title"),
        testId: "menu-item-auto-collection-template",
        icon: PrimeIcons.BARS,
        routerLink: [`collections/collection-auto-filter-templates/${this.settings.organizationId}`],
        routerLinkActiveOptions: { exact: false },
        permissions: [UserPermission.ProcessAdministrator],
      }),
      ...(this.settings.isProcessActionEnabled
        ? [
            this.licensed({
              label: translateSafely("processAction.title"),
              testId: "menu-item-process-action",
              icon: PrimeIcons.BOLT,
              routerLink: [`process-actions/${this.settings.organizationId}`],
              routerLinkActiveOptions: { exact: false },
              permissions: [UserPermission.ProcessAdministrator],
            }),
          ]
        : []),
      this.licensed({
        label: translateSafely("emailSendOutRule.title"),
        testId: "menu-item-email-send-out-rule",
        icon: PrimeIcons.ENVELOPE,
        routerLink: [`email-send-out-rules/${this.settings.organizationId}`],
        routerLinkActiveOptions: { exact: false },
        permissions: [UserPermission.ProcessAdministrator],
      }),
      this.licensed({
        label: translateSafely("processMilestoneList.milestones"),
        testId: "menu-item-milestones",
        icon: PrimeIcons.TROPHY,
        routerLink: [`processes/milestone-list/${this.settings.organizationId}`],
        routerLinkActiveOptions: { exact: false },
        permissions: [UserPermission.ProcessAdministrator],
      }),
    ],
    [MenuSection.Templates]: [
      this.licensed({
        label: translateSafely("documents.title"),
        testId: "menu-item-general-documents",
        icon: PrimeIcons.FILE,
        routerLink: ["documents/general"],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.ContentAdministrator],
        tooltip: translateSafely("documents.menuTooltip"),
      }),
      this.licensed({
        label: translateSafely("documentTemplates.title"),
        testId: "menu-item-document-templates",
        icon: PrimeIcons.FOLDER,
        routerLink: ["documents/templates"],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.ContentAdministrator],
        tooltip: translateSafely("documentTemplates.menuTooltip"),
      }),
      this.licensed({
        label: translateSafely("formTemplates.title"),
        testId: "menu-item-form-templates",
        icon: PrimeIcons.FILE_PDF,
        routerLink: ["documents/forms"],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.ContentAdministrator],
        tooltip: translateSafely("formTemplates.menuTooltip"),
      }),
      this.licensed({
        label: translateSafely("requiredDocument.menuTitle"),
        testId: "menu-item-required-document-configuration",
        icon: PrimeIcons.FILE,
        routerLink: [`documents/required/${this.settings.organizationId}`],
        permissions: [UserPermission.Administrator],
      }),
      this.licensed({
        label: translateSafely("emailTemplates.title"),
        testId: "menu-item-email-templates",
        icon: PrimeIcons.ENVELOPE,
        routerLink: ["documents/emails"],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.ContentAdministrator],
      }),
      this.licensed({
        label: translateSafely("customDocumentTypes.title"),
        testId: "menu-item-user-defined-document-types",
        icon: PrimeIcons.FILE_EDIT,
        routerLink: [`documents/custom-types`],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.ContentAdministrator],
      }),
      this.licensed({
        label: translateSafely("laborAgreements.title"),
        testId: "menu-item-collective-and-framework-agreements",
        icon: PrimeIcons.CLIPBOARD,
        routerLink: [`labor-agreements`],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.ContentAdministrator],
      }),
      // this.licensed({
      //   label: translateSafely("onlineApplication.title"),
      //   icon: PrimeIcons.DESKTOP,
      //   routerLink: [`documents/online-application`],
      //   routerLinkActiveOptions: { exact: true },
      //   permissions: [UserPermission.ContentAdministrator],
      // }),
    ],
    [MenuSection.Education]: [
      this.licensed({
        testId: "menu-item-module",
        label: translateSafely("educationModules.title"),
        icon: PrimeIcons.LIST,
        routerLink: ["education/modules"],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.Administrator, UserPermission.CourseAdministrator, UserPermission.CourseTeacher],
      }),
      {
        testId: "menu-item-course",
        label: translateSafely("educationCourses.title"),
        icon: PrimeIcons.TICKET,
        routerLink: ["education/courses"],
        routerLinkActiveOptions: { exact: true },
        permissions: [
          UserPermission.Administrator,
          UserPermission.CourseAdministrator,
          UserPermission.CourseTeacher,
          UserPermission.CourseObserver,
        ],
      },
      {
        testId: "menu-item-exam",
        label: translateSafely("educationExams.title"),
        icon: PrimeIcons.CALENDAR,
        routerLink: ["education/exams"],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.Administrator, UserPermission.CourseAdministrator, UserPermission.CourseTeacher],
      },
      {
        testId: "menu-item-audit",
        label: translateSafely("educationExamResults.title"),
        icon: PrimeIcons.CALCULATOR,
        routerLink: ["education/exam-results/overview"],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.Administrator, UserPermission.CourseAdministrator, UserPermission.CourseTeacher],
      },
      {
        label: translateSafely("educationManagement.title"),
        icon: PrimeIcons.BOOK,
        routerLink: ["education"],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.Candidate],
      },
    ],
    [MenuSection.Settings]: [
      {
        label: translateSafely("organization.my"),
        icon: PrimeIcons.MAP_MARKER,
        routerLink: ["settings/organizations/my"],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.Administrator],
        testId: "menu-item-organization",
      },
      this.licensed({
        label: translateSafely("billing.title"),
        icon: PrimeIcons.EURO,
        routerLink: [`settings/organizations/invoice/${this.settings.organizationId}`],
        routerLinkActiveOptions: { exact: false },
        permissions: [UserPermission.BillingAdministrator],
        testId: "menu-item-invoice",
      }),
      this.licensed(
        {
          label: translateSafely("professions.my"),
          icon: PrimeIcons.ID_CARD,
          routerLink: [`settings/professions/my`],
          routerLinkActiveOptions: { exact: true },
          permissions: [UserPermission.Administrator],
          testId: "menu-item-profession",
        },
        "professions"
      ),
      this.licensed({
        label: translateSafely("experiences.title"),
        icon: PrimeIcons.GRADUATION_CAP,
        routerLink: [`settings/professional-fields/${this.settings.organizationId}`],
        routerLinkActiveOptions: { exact: false },
        permissions: [UserPermission.Administrator],
        testId: "menu-item-experience",
      }),
      {
        label: translateSafely("organizations.title"),
        icon: PrimeIcons.TH_LARGE,
        routerLink: [`settings/organizations/${this.settings.organizationId}`],
        routerLinkActiveOptions: { exact: false },
        permissions: [UserPermission.Administrator, UserPermission.PartnerAdministrator, UserPermission.CrmContributor],
        testId: "menu-item-customer",
      },
      {
        label: translateSafely("users.title"),
        icon: PrimeIcons.USER,
        routerLink: [`settings/users/${this.settings.organizationId}`],
        routerLinkActiveOptions: { exact: false },
        permissions: [UserPermission.Administrator],
        testId: "menu-item-user",
      },
      {
        label: translateSafely("userGroups.title"),
        icon: PrimeIcons.USERS,
        routerLink: [`settings/user-groups/${this.settings.organizationId}`],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.Administrator],
        testId: "menu-item-user-group",
      },
      ...(ENABLE_CHAT
        ? [
            {
              label: translateSafely("chatConfiguration.title"),
              icon: PrimeIcons.COMMENT,
              routerLink: [`settings/chat/${this.settings.organizationId}`],
              routerLinkActiveOptions: { exact: true },
              permissions: [UserPermission.Administrator],
              testId: "menu-item-chat-configuration",
            },
          ]
        : []),
      this.licensed({
        label: translateSafely("consent.title"),
        icon: PrimeIcons.EXCLAMATION_CIRCLE,
        routerLink: [`settings/consent/${this.settings.organizationId}`],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.Administrator],
        testId: "menu-item-permission",
      }),
      this.licensed(
        {
          label: translateSafely("auditDefinitions.title"),
          icon: PrimeIcons.SHIELD,
          routerLink: [`settings/audit-definitions/${this.settings.organizationId}`],
          routerLinkActiveOptions: { exact: false },
          permissions: [UserPermission.Administrator],
          testId: "menu-item-audit-definitions",
        },
        "gueteSiegel"
      ),
      this.licensed({
        label: translateSafely("documentRequirements.title"),
        icon: PrimeIcons.FILE_EDIT,
        routerLink: [`settings/document-requirements/${this.settings.organizationId}`],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.Administrator],
        testId: "menu-item-document-requirements",
      }),
      this.licensed({
        label: translateSafely("vaccinationConfig.title"),
        icon: PrimeIcons.HEART,
        routerLink: [`settings/vaccination-config/${this.settings.organizationId}`],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.Administrator],
        testId: "menu-item-vaccination-config",
      }),
      this.licensed({
        label: translateSafely("profileAccess.title"),
        icon: PrimeIcons.USER_EDIT,
        routerLink: [`settings/candidate-profile-config/${this.settings.organizationId}/candidate`],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.Administrator],
        testId: "menu-item-profile-access",
      }),
      this.licensed({
        label: translateSafely("localPartnerProfileAccess.title"),
        icon: PrimeIcons.USER_EDIT,
        routerLink: [`settings/local-partner-profile-config/${this.settings.organizationId}`],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.Administrator],
        testId: "menu-item-local-partner-profile-access",
      }),
      this.licensed({
        label: translateSafely("candidateApplicationMaskConfig.title"),
        icon: PrimeIcons.USER_PLUS,
        routerLink: [`settings/candidate-application-mask/${this.settings.organizationId}`],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.Administrator],
        testId: "menu-item-candidate-application-mask-config",
      }),
      this.licensed({
        label: translateSafely("systemSettings.title"),
        icon: PrimeIcons.COG,
        routerLink: [`settings/organizations/system-settings/${this.settings.organizationId}`],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.Administrator],
        testId: "menu-item-system-settings",
      }),
      this.licensed({
        label: translateSafely("apiConfig.title"),
        icon: PrimeIcons.WAVE_PULSE,
        routerLink: [`settings/api-config/${this.settings.organizationId}`],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.Administrator],
      }),
      this.licensed({
        label: translateSafely("sharingPresets.title"),
        icon: PrimeIcons.OBJECTS_COLUMN,
        routerLink: [`settings/sharing-presets/${this.settings.organizationId}`],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.Administrator, UserPermission.PartnerAdministrator],
        testId: "menu-item-sharing-presets",
      }),
      this.licensed({
        label: translateSafely("addOnServices.title"),
        icon: PrimeIcons.MICROCHIP,
        routerLink: [`settings/add-on-services/${this.settings.organizationId}`],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.Administrator],
      }),
      ...(this.settings.isMasterUser && this.settings.isMasterOrganization
        ? [
            {
              label: translateSafely("ankaadiaTools.title"),
              icon: PrimeIcons.WRENCH,
              routerLink: [`settings/ankaadia-tools/${this.settings.organizationId}`],
              routerLinkActiveOptions: { exact: true },
              permissions: [UserPermission.Administrator],
            },
          ]
        : []),
    ],
    [MenuSection.Notifications]: [
      {
        label: translateSafely("dashboard.title"),
        icon: PrimeIcons.HOME,
        routerLink: [`dashboard`],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.User],
      },
      {
        label: translateSafely("activityLog.title"),
        icon: PrimeIcons.INFO_CIRCLE,
        routerLink: [`activity`],
        routerLinkActiveOptions: { exact: true },
        permissions: [UserPermission.User],
      },
    ],
    [MenuSection.TalentPool]: [
      this.licensed(
        {
          label: translateSafely("talentPools.view"),
          icon: PrimeIcons.USERS,
          routerLink: [`talent-pools/view`],
          routerLinkActiveOptions: { exact: true },
          permissions: [UserPermission.User],
        },
        "talentPool"
      ),
      this.licensed(
        {
          label: translateSafely("talentPools.edit"),
          icon: PrimeIcons.ID_CARD,
          routerLink: ["talent-pools/edit"],
          routerLinkActiveOptions: { exact: true },
          permissions: [UserPermission.PartnerAdministrator],
        },
        "talentPool"
      ),
    ],
  };

  private readonly SectionData: Record<MenuSection, CustomMenuItem> = {
    [MenuSection.Candidates]: { label: translateSafely("candidates.title"), icon: PrimeIcons.ID_CARD },
    [MenuSection.Processes]: {
      label: translateSafely("processes.title"),
      icon: PrimeIcons.BRIEFCASE,
      badge: this.taskInboxCount$,
    },
    [MenuSection.Education]: { label: translateSafely("educationManagement.title"), icon: PrimeIcons.BOOK },
    [MenuSection.Templates]: { label: translateSafely("templates.menuTitle"), icon: PrimeIcons.FOLDER_OPEN },
    [MenuSection.Settings]: { label: translateSafely("settings.title"), icon: PrimeIcons.COG },
    [MenuSection.Notifications]: { label: translateSafely("notifications.title"), icon: PrimeIcons.BELL },
    [MenuSection.Tasks]: {
      label: translateSafely("tasks.title"),
      icon: PrimeIcons.BRIEFCASE,
      badge: this.taskInboxCount$,
    },
    [MenuSection.Profile]: { label: translateSafely("candidate.profile"), icon: PrimeIcons.PENCIL },
    [MenuSection.EmployerSearch]: { label: translateSafely("employerSearch.title"), icon: PrimeIcons.LINK },
    [MenuSection.Documents]: { label: translateSafely("attachments.title"), icon: PrimeIcons.FILE },
    [MenuSection.TalentPool]: { label: translateSafely("talentPool.title"), icon: PrimeIcons.USERS },
  };

  private readonly menuSource = new Subject<string>();
  private readonly resetSource = new Subject();

  helpUrl: string;
  menuSource$ = this.menuSource.asObservable();
  resetSource$ = this.resetSource.asObservable();

  constructor(
    private readonly settings: SettingsService,
    private readonly orgFactory: OrganizationFactoryService,
    private readonly taskInboxService: TaskInboxService
  ) {
    this.orgFactory
      .getOrganizationSpecifics(this.settings.organizationId)
      .subscribe((x) => (this.helpUrl = x.getHelpUrl()));
  }

  onMenuStateChange(key: string): void {
    this.menuSource.next(key);
  }

  reset(): void {
    this.resetSource.next(undefined);
  }

  getSections(): IndexedMenuItem[] {
    return Object.values(MenuSection)
      .filter((x) => x !== MenuSection.TalentPool)
      .filter((x) => this.settings.educationModuleVisible || x !== MenuSection.Education)
      .map((section, i) => ({
        index: i,
        label: this.SectionData[section].label,
        icon: this.SectionData[section].icon,
        badge: this.SectionData[section].badge,
        items: this.filter(this.MenuSectionMap[section]),
        routerLink: this.SectionData[section].routerLink,
        routerLinkActiveOptions: this.SectionData[section].routerLinkActiveOptions,
        permissions: this.SectionData[section].permissions,
        name: section.toLowerCase(),
      }))
      .filter((x) => this.settings.hasAnyPermission(x.permissions));
  }

  private licensed<T extends CustomMenuItem>(item: T, module?: AdditionalModule): T {
    return this.settings.isLicensed && this.settings.hasModule(module) ? item : null;
  }

  private filter<T extends CustomMenuItem>(items: T[]): T[] {
    return items.filter((x) => x).filter((x) => this.settings.hasAnyPermission(x.permissions));
  }

  private getTaskInboxCount(): Observable<string> {
    return this.taskInboxService.counter.pipe(this.asBadgeCounter());
  }

  private asBadgeCounter(): OperatorFunction<number, string> {
    return (source) =>
      source.pipe(
        map((count) => {
          if (count === 0) return null;
          if (count > 10) return "10+";
          return count.toString();
        })
      );
  }
}
